import { Dispatch, FC, MutableRefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { LearnosityRequest } from '../../models/LearnosityRequest';
import { DiagnosticSteps } from '../../models/DiagnosticStepsEnum';
import { assessmentSubmitted } from '../../bffApiCalls/assessmentSubmitted';
import { useAuth0 } from '@auth0/auth0-react';
import { isTokenExpired } from '../../utils/isTokenExpired';
import refreshTokenOrLogout from '../../utils/refreshTokenOrLogout';
import { PencilRuler } from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { DataLrnSession } from '../../custom-hooks/useLrnSession';
import type { ItemsAppInterface } from '../../vite-env';
import { DiagnosticStateEnum } from '../DiagnosticUI/Diagnostic';
import {
  useLearnosityItemsInitialization,
  type LearnosityInitializationRefetch,
} from '../../custom-hooks/useLearnosityItemsInitialization';
import { Dialog } from 'primereact/dialog';
import EphemeralMessage from '../StudentSingleSkill/EphemeralMessage';
import { useDiagnosticUserAction } from '../../custom-hooks/useDiagnosticUserAction';
import { useObservationLogger } from '../../experiments/useObservationLogger';
import AutoSubmitOverlay from './AutoSubmitOverlay/AutoSubmitOverlay';
import { usePostHog } from '@posthog/react';
import { useDgEndEarlyConfig, decideEndEarly, gateFor } from '../../businessLogicService/diagnosticEndEarly';
import { getDiagnosticAbilityEstimate } from '../../bffApiCalls/getDiagnosticAbilityEstimate';
import { useSuspiciousActivityTracking } from '../../custom-hooks/useSuspiciousActivityTracking';
import { useExitFullscreenOnUnmount } from '../../custom-hooks/useExitFullscreenOnUnmount';
import { useCopyProtection } from '../../custom-hooks/useCopyProtection';
import { addLearnosityBreadcrumb, type LearnosityTelemetryFields } from '../../utils/learnositySentry';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI';

interface AssessmentWrapperProps {
  request: LearnosityRequest;
  refetchInitialization: LearnosityInitializationRefetch;
  setStep: (step: DiagnosticSteps) => void;
  sessionInfo: DataLrnSession;
  setDiagnosticState: Dispatch<SetStateAction<DiagnosticStateEnum | null>>;
}

type EndEarlyDecisionContext = Required<
  Pick<LearnosityTelemetryFields, 'question' | 'gate' | 'abilityEstimate' | 'standardError'>
>;

function decisionContext(
  question: number,
  gate: EndEarlyDecisionContext['gate'],
  abilityEstimate: number | null,
  standardError: number | null,
): EndEarlyDecisionContext {
  return {
    question,
    gate,
    abilityEstimate: typeof abilityEstimate === 'number' && Number.isFinite(abilityEstimate) ? abilityEstimate : null,
    standardError:
      typeof standardError === 'number' && Number.isFinite(standardError) && standardError >= 0 ? standardError : null,
  };
}

const DiagnosticAssessmentWrapper: FC<AssessmentWrapperProps> = ({
  request,
  refetchInitialization,
  setStep,
  sessionInfo,
  setDiagnosticState,
}) => {
  const { getAccessTokenSilently, logout } = useAuth0();
  const userRedux = useAppSelector(selectUser);
  const [testQuestion, setTestQuestion] = useState(0);
  const [skippedQuestions, setSkippedQuestions] = useState(0);
  const [skippedModalVisible, setSkippedModalVisible] = useState(false);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const { logStarted, logSubmitted, logResumed, logEndedEarly } = useDiagnosticUserAction();
  const { logObservations } = useObservationLogger();
  const posthog = usePostHog();
  useSuspiciousActivityTracking({ diagnosticSessionId: sessionInfo.sessionId });
  useExitFullscreenOnUnmount();
  useCopyProtection();

  // --- Diagnostic end-early: auto-submit once the ability-estimate criteria are met (TEAMB-3395) ---
  const cfg = useDgEndEarlyConfig();
  // Full-screen celebratory animation shown while the shortened diagnostic submits.
  const [autoSubmitVisible, setAutoSubmitVisible] = useState(false);
  // Gates the end-early checks until the Learnosity player is ready, so we never fire a check (and a
  // no-op submit) during initial load — e.g. when a session resumes already past a gate.
  // True once the diagnostic has actually submitted — gates the "Show me my results" button so a failed
  // submit can never advance the student to results with an unsubmitted diagnostic.
  const [submitReady, setSubmitReady] = useState(false);
  const autoSubmittedRef = useRef(false);
  // Set if an auto-submit fails, so we don't re-trigger it and the manual finish can navigate normally.
  const autoSubmitAbortedRef = useRef(false);
  const nextCheckAtRef = useRef<number>(Math.min(cfg.firstGateQ, cfg.universalQ));
  const endEarlyInitializedRef = useRef(false);
  const lastAutoDecisionRef = useRef<EndEarlyDecisionContext>(decisionContext(0, 'early', null, null));
  // The diagnostic UI; made inert while the celebration overlay is up so keyboard/SR focus can't reach
  // the hidden assessment behind it (the overlay is portaled to <body> and the app header sits above
  // this tree, so both stay interactive).
  const rootRef = useRef<HTMLDivElement>(null);

  const handleShowTooltip = () => {
    setTimeout(() => {
      setIsTooltipActive(true);
    }, 600);

    setTimeout(() => {
      setIsTooltipActive(false);
    }, 3500);
  };

  useEffect(() => {
    handleShowTooltip();
  }, []);

  // Toggle inert on the diagnostic content whenever the celebration overlay shows/hides.
  useEffect(() => {
    if (rootRef.current) rootRef.current.inert = autoSubmitVisible;
  }, [autoSubmitVisible]);

  useEffect(() => {
    if (sessionInfo.count) {
      setTestQuestion(sessionInfo.count);
    } else {
      setTestQuestion(1);
    }
  }, [sessionInfo.count]);

  const checkTokenRefresh = async () => {
    if (isTokenExpired()) {
      await refreshTokenOrLogout(getAccessTokenSilently, logout, posthog);
    }
  };

  const saveAndQuit = () => {
    itemsAppRef.current?.save();
    setDiagnosticState(DiagnosticStateEnum.SAVEANDQUIT);
    setStep(DiagnosticSteps.DEMONSTRATE);
  };

  const isSkippedQuestion = (itemsAppRef: MutableRefObject<ItemsAppInterface | null>) => {
    if (itemsAppRef.current) {
      const questions = Object.keys(itemsAppRef.current.getQuestions());
      const responses = Object.keys(itemsAppRef.current.getResponses());
      const lastQuestion = questions[questions.length - 1] || null;
      return !responses.find(key => key === lastQuestion);
    }
  };

  const goToNext = async () => {
    if (itemsAppRef.current) {
      if (isSkippedQuestion(itemsAppRef) && skippedQuestions === 0) {
        setSkippedModalVisible(true);
      } else {
        setLoadingQuestion(true);
        itemsAppRef.current
          .items()
          .next()
          .catch(() => {
            itemsAppRef.current?.submit({ show_submit_confirmation: false, show_submit_ui: false });
          });
      }
    }
  };

  const logEndEarlyBreadcrumb = (
    fields: Pick<
      LearnosityTelemetryFields,
      'action' | 'question' | 'nextCheckAt' | 'gate' | 'abilityEstimate' | 'standardError' | 'submissionMode'
    > = {},
  ) =>
    addLearnosityBreadcrumb('diagnostic_end_early', {
      assessmentType: 'diagnostic',
      learnerId: request.request.user_id,
      sessionId: request.request.session_id,
      ...fields,
    });

  // Criteria met → end the diagnostic now (no student choice): record it, show the celebration overlay,
  // and submit. Fires at most once per session (guarded by autoSubmittedRef).
  const autoSubmit = (
    q: number,
    gate: 'early' | 'late' | 'universal',
    theta: number | null,
    standardError: number | null,
  ) => {
    autoSubmittedRef.current = true;
    const decision = decisionContext(q, gate, theta, standardError);
    lastAutoDecisionRef.current = decision;
    logEndEarlyBreadcrumb({
      action: 'auto_submit',
      ...decision,
      submissionMode: 'automatic',
    });
    setAutoSubmitVisible(true);
    // ENDED_EARLY is recorded only once the submit succeeds (test:submit:success), so a failed submit
    // isn't reported as a completed early finish.
    itemsAppRef.current?.submit({ show_submit_confirmation: false, show_submit_ui: false });
  };

  // Read the ability estimate at question q, then auto-submit or schedule the next check (pure policy).
  const runEndEarlyCheck = async (q: number) => {
    const result = await getDiagnosticAbilityEstimate(
      sessionInfo.sessionId,
      getAccessTokenSilently,
      logout,
      posthog,
      request.request.user_id,
    );
    const theta = result?.abilityEstimate ?? null;
    const standardError = result?.standardError ?? null;

    const decision = decideEndEarly(q, theta, cfg);
    if (decision.kind === 'show') {
      autoSubmit(q, decision.gate, theta, standardError);
    } else {
      nextCheckAtRef.current = decision.nextCheckAt;
      const context = decisionContext(q, gateFor(q, cfg), theta, standardError);
      logEndEarlyBreadcrumb({
        action: 'check',
        ...context,
        nextCheckAt: decision.nextCheckAt,
      });
    }
  };

  // On (re)entry, kick off the ability-estimate checks. No prompt state to restore on resume: an
  // auto-submitted diagnostic is already complete, so a resumed session simply hasn't hit a gate yet.
  // Start at the earlier of the first θ gate and the universal gate, so a QA-lowered universalQ
  // (e.g. 5) triggers even though it's before firstGateQ.
  const initEndEarly = async (q: number) => {
    const startAt = Math.min(cfg.firstGateQ, cfg.universalQ);
    if (q >= startAt) {
      await runEndEarlyCheck(q);
    } else {
      nextCheckAtRef.current = startAt;
    }
  };

  const { status, itemsAppRef } = useLearnosityItemsInitialization({
    reloadOn10001AfterReady: true,
    initObj: request,
    assessmentType: 'diagnostic',
    refetchInitialization,
    getContainer: () => document.getElementById('learnosity_assess'),
    onReady: (itemsApp, telemetry) => {
      if (request.request.state === 'resume' || sessionInfo.status === 'Incomplete') {
        logResumed(sessionInfo.sessionId);
      } else {
        setTimeout(() => {
          logStarted(sessionInfo.sessionId);
          // A resume is not a new start, so only the fresh-session branch logs one.
          logObservations('engagement_metrics', [{ subject: 'diagnostic', type: 'diagnostic_start' }]);
        }, 5000);
      }
      itemsApp.on('test:start', function () {
        checkTokenRefresh();
      });
      itemsApp.on('item:changed', () => {
        setLoadingQuestion(false);
        setTestQuestion(prev => prev + 1);
        checkTokenRefresh();
        itemsApp.save();
      });
      itemsApp.on('test:submit:success', () => {
        const sentryTraceId =
          Sentry.getActiveSpan()?.spanContext().traceId ?? Sentry.getCurrentScope().getPropagationContext().traceId;
        assessmentSubmitted(
          sessionInfo?.sessionId,
          'diagnostic',
          getAccessTokenSilently,
          logout,
          posthog,
          undefined,
          sentryTraceId,
          request.request.user_id,
        );
        logSubmitted(sessionInfo.sessionId);
        logObservations('engagement_metrics', [{ subject: 'diagnostic', type: 'diagnostic_complete' }]);
        localStorage.setItem('diagnostic_finished', 'true');
        if (autoSubmittedRef.current) {
          // Auto-submit confirmed: now it's safe to record the early finish, and the celebration's
          // "Show me my results" button (gated on submitReady) can advance to FINISHED.
          const decision = lastAutoDecisionRef.current;
          logEndedEarly(sessionInfo.sessionId, {
            question: decision.question,
            theta: decision.abilityEstimate,
            standardError: decision.standardError,
            gate: decision.gate,
          });
          setSubmitReady(true);
        } else {
          // A normal (full-length) finish advances straight through.
          setStep(DiagnosticSteps.FINISHED);
        }
      });
      itemsApp.on('test:submit:error', e => {
        const wasAutomatic = autoSubmittedRef.current;
        const decision = wasAutomatic ? lastAutoDecisionRef.current : undefined;
        logEndEarlyBreadcrumb({
          action: 'submit_error',
          submissionMode: wasAutomatic ? 'automatic' : 'manual',
          ...decision,
        });
        telemetry.captureError(e);
        // Abort the auto-submit and return the student to the assessment. We don't re-trigger it; a
        // later successful (manual) submit navigates via the normal path above.
        autoSubmittedRef.current = false;
        autoSubmitAbortedRef.current = true;
        setAutoSubmitVisible(false);
      });
    },
  });

  const playerReady = status === 'ready';

  // Runs on each question change (item:changed increments testQuestion) and once the player is ready.
  // Gated on playerReady so no check/submit fires during initial load.
  useEffect(() => {
    if (!playerReady || testQuestion < 1 || autoSubmittedRef.current || autoSubmitAbortedRef.current) return;
    if (!endEarlyInitializedRef.current) {
      endEarlyInitializedRef.current = true;
      void initEndEarly(testQuestion);
    } else if (testQuestion >= nextCheckAtRef.current) {
      void runEndEarlyCheck(testQuestion);
    }
  }, [testQuestion, playerReady]);

  return (
    <div
      ref={rootRef}
      className="tw-w-[80%] tw-mx-auto tw-bg-white tw-p-6 tw-mt-[6vh] tw-rounded-[34px] tw-border tw-border-solid tw-border-4 tw-border-indigo-200"
    >
      <div className="tw-flex tw-justify-between">
        <div className="tw-flex tw-items-center tw-ml-8">
          <PencilRuler size={20} />
          <h1 className="tw-mx-2">Diagnostic</h1>
          <span>{userRedux?.gradeLevel?.name} diagnostic</span>
        </div>
        {testQuestion === 17 && (
          <div style={{ zIndex: 10 }}>
            <EphemeralMessage
              delay={0}
              customStyle={{ right: '15vw', top: '14vh' }}
              text="Need a break? Save your progress and pick up where you left off"
              ease={[0.34, 1, 1, 1]}
              duration={3}
              stroke={false}
            />
          </div>
        )}
        <Button label="Save my progress" onClick={saveAndQuit} outlined className="tw-border-0 tw-mr-8" />
      </div>
      <div
        className="tw-w-[95%] tw-mt-2 tw-flex tw-items-center tw-py-1 tw-mx-auto tw-border tw-border-[2px] tw-border-solid tw-rounded-full tw-px-2 tw-border-neutral-200"
        id="diagnostic-progress-bar"
      >
        <ProgressBar
          style={{
            height: '1rem',
            width: '100%',
          }}
          pt={{
            value: { 'data-tooltip': `${testQuestion}` },
          }}
          showValue={false}
          value={testQuestion <= 34 ? testQuestion * 2.94 : 100}
          mode="determinate"
          className={`inline-block custom-progress-bar ${isTooltipActive ? 'show-tooltip' : ''}`}
          onMouseOver={() => setIsTooltipActive(true)}
          onMouseLeave={() => setIsTooltipActive(false)}
        ></ProgressBar>
      </div>
      <div>
        {status === 'failed' && <ErrorBoundaryUI embedded />}
        <div id="learnosity_assess" className="!tw-relative tw-z-[2]" hidden={status === 'failed'}></div>
      </div>
      <div className="tw-flex tw-justify-end">
        {(testQuestion === 5 || testQuestion === 25) && (
          <EphemeralMessage
            delay={0}
            customStyle={{ right: '20vw', top: '60vh' }}
            text="If a question feels tricky, just click Next Question, no guessing needed"
            ease={[0.34, 1, 1, 1]}
            duration={3}
            stroke={false}
          />
        )}

        <Button
          label={testQuestion === 34 ? 'Finish Diagnostic' : 'Next Question'}
          onClick={goToNext}
          disabled={loadingQuestion}
        />
      </div>
      <Dialog
        className="tw-w-[35%]"
        showHeader={false}
        visible={skippedModalVisible}
        onHide={() => setSkippedModalVisible(false)}
      >
        <div className="tw-w-full tw-p-4">
          <h1>Move on without answering?</h1>
          <p className="tw-w-[80%]">
            {`Just know`} <b>{`skipped questions don't come back later`}</b>
            {`, this one's done once you click Move on.`}
          </p>
          <div className="tw-flex tw-justify-end">
            <Button onClick={() => setSkippedModalVisible(false)} outlined label="Let me try again" />
            <Button
              onClick={() => {
                if (itemsAppRef.current) {
                  setLoadingQuestion(true);
                  setSkippedQuestions(prev => prev + 1);
                  setSkippedModalVisible(false);
                  itemsAppRef.current
                    .items()
                    .next()
                    .catch(() => {
                      itemsAppRef.current?.submit({ show_submit_confirmation: false, show_submit_ui: false });
                    });
                }
              }}
              label="Move on"
              className="tw-ml-2"
            />
          </div>
        </div>
      </Dialog>

      {/* Full-screen celebration shown when the diagnostic auto-submits early (TEAMB-3395):
          confetti Lottie → "Show me my results" → the existing finished/roadmap flow. */}
      <AutoSubmitOverlay
        visible={autoSubmitVisible}
        submitReady={submitReady}
        onShowResults={() => setStep(DiagnosticSteps.FINISHED)}
      />
    </div>
  );
};

export default DiagnosticAssessmentWrapper;
